import { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { getSelectionText, getDocumentAsFile, convertBlobToBase64 } from "../utils/wordOperations";
import { getAuthenticatedHeaders } from "../utils/authentication";
import type { FileWithUpload } from "../types/FileWithUpload";
import type { ModeId } from "../types/Mode";
import { useAuthContext } from "../components/AuthProvider";
import { CHAT_V4_API_ENDPOINT } from "../../constant/api-endpoints";
import { parseSSE } from "../utils/sse";
import type { StreamChunk } from "../types/StreamChunk";
import type { HistoryMessage, AIHistoryMessage, ToolHistoryMessage } from "../types/HistoryMessage";
import type { ChatRequestPayload } from "../types/ChatRequest";

interface UseChatStreamProps {
  modeId: ModeId;
  onStart?: () => void;
  setConversationId: (id: string | null) => void;
  setMessages: (
    messages: HistoryMessage[] | ((prev: HistoryMessage[]) => HistoryMessage[])
  ) => void;
  messages: HistoryMessage[];
  conversationId: string | null;
}

interface UseChatStreamReturn {
  loading: boolean;
  error: string | null;
  submitChat: (query: string, fileIds?: string[]) => Promise<void>;
  startNewConversation: () => void;
  stopStreaming: () => void;
  isThinking: boolean;
}

export const useChatStream = ({
  modeId,
  onStart,
  setConversationId,
  setMessages,
  messages,
  conversationId,
}: UseChatStreamProps): UseChatStreamReturn => {
  const { session } = useAuthContext();
  const conversationIdRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Sync ref with prop
  useEffect(() => {
    if (conversationId !== undefined) {
      conversationIdRef.current = conversationId;
    }
  }, [conversationId]);

  const processEvent = useCallback(
    (event: StreamChunk) => {
      if (event.type === "conversation_id") {
        const newId = event.conversation_id;
        setConversationId(newId);
        conversationIdRef.current = newId;
        return;
      }

      if (event.type === "error") {
        throw new Error(event.error);
      }

      if (event.type === "delta") {
        setMessages((prev) => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg?.type === "ai") {
            const updatedMsg: AIHistoryMessage = {
              ...lastMsg,
              content: lastMsg.content + event.content,
            };
            return [...prev.slice(0, -1), updatedMsg];
          } else {
            const newAIMessage: AIHistoryMessage = {
              id: uuidv4(),
              type: "ai",
              content: event.content,
            };
            return [...prev, newAIMessage];
          }
        });
        return;
      }

      if (event.type === "tool_start") {
        const toolId = event.run_id;
        setMessages((prev) => {
          const toolIndex = prev.findIndex((m) => m.type === "tool" && m.tool_call_id === toolId);
          if (toolIndex !== -1) return prev;

          const toolMessage: ToolHistoryMessage = {
            id: uuidv4(),
            type: "tool",
            tool_call_id: toolId,
            tool_name: event.tool_name,
            content: "",
            status: "loading",
            args:
              event.args && typeof event.args === "object" && event.args !== null
                ? (event.args as Record<string, unknown>)
                : undefined,
          };
          return [...prev, toolMessage];
        });
        return;
      }

      if (event.type === "tool_end") {
        const toolId = event.run_id;
        setMessages((prev) => {
          const toolIndex = prev.findIndex((m) => m.type === "tool" && m.tool_call_id === toolId);
          const outputContent =
            typeof event.content === "string" ? event.content : JSON.stringify(event.content);

          if (toolIndex !== -1) {
            const toolMsg = prev[toolIndex] as ToolHistoryMessage;
            const updatedToolMsg: ToolHistoryMessage = {
              ...toolMsg,
              content: outputContent,
              status: "completed",
            };
            return [...prev.slice(0, toolIndex), updatedToolMsg, ...prev.slice(toolIndex + 1)];
          } else {
            const toolMessage: ToolHistoryMessage = {
              id: uuidv4(),
              type: "tool",
              tool_call_id: toolId,
              tool_name: event.tool_name,
              content: outputContent,
              status: "completed",
            };
            return [...prev, toolMessage];
          }
        });
        return;
      }
    },
    [setConversationId, setMessages]
  );

  const startNewConversation = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setConversationId(null);
    conversationIdRef.current = null;
    setMessages([]);
    setError(null);
  }, [setConversationId, setMessages]);

  const submitChat = async (query: string, fileIds: string[] = []): Promise<void> => {
    if (loading || (!query.trim() && modeId === "agent")) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError(null);

    const [selection, editingFile] = await Promise.all([getSelectionText(), getDocumentAsFile()]);

    const userMessage: HistoryMessage = {
      id: uuidv4(),
      type: "human",
      content: query.trim(),
      file_ids: fileIds.length > 0 ? [...fileIds] : undefined,
    };

    setMessages((prev) => [...prev, userMessage]);

    if (onStart) {
      onStart();
    }

    try {
      const headers = await getAuthenticatedHeaders(session);

      let editing_document = null;
      if (editingFile) {
        const base64 = await convertBlobToBase64(editingFile);
        editing_document = {
          filename: "document.docx",
          content: base64,
        };
      }

      const payload: ChatRequestPayload = {
        query: query.trim(),
        mode: modeId,
        conversation_id: conversationIdRef.current,
        file_ids: fileIds,
        editing_document,
        editing_document_selection: selection || null,
        checkpoint_id: null,
      };

      const response = await fetch(CHAT_V4_API_ENDPOINT, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} error`);
      }
      if (!response.body) {
        throw new Error("ReadableStream not supported");
      }

      await parseSSE<StreamChunk>(response.body, processEvent);
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        // Handled by cleanup
      } else {
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    } finally {
      setLoading(false);
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const isThinking =
    loading &&
    messages.length > 0 &&
    (messages[messages.length - 1].type === "human" ||
      messages[messages.length - 1].type === "tool");

  return {
    loading,
    error,
    submitChat,
    startNewConversation,
    stopStreaming,
    isThinking,
  };
};
